'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/calculus'

const SecantToTangent = dynamic(() => import('../_components/widgets/CalcWidgets').then(m => m.SecantToTangent), { ssr: false })
const LocalLinearity = dynamic(() => import('../_components/widgets/CalcWidgets').then(m => m.LocalLinearity), { ssr: false })

export default function SlopeOfACurve() {
  return (
    <Lesson>
      <Step>
        <H>The question that powers all of AI</H>
        <P>
          Training a model means adjusting its parameters to make the loss smaller. With millions of parameters, you can’t try
          every setting. You need to know, for each parameter: <strong>if I nudge this a little, does the loss go up or down —
          and how fast?</strong>
        </P>
        <P>
          That “how fast does the output change when I nudge the input” is a <strong>slope</strong>. For a straight line it’s
          one number (Module 2). But for a curve the steepness keeps changing. This lesson is about measuring the slope of a
          curve <em>at a single point</em>. It’s called the <strong>derivative</strong>, and it’s the core idea of calculus.
        </P>
      </Step>

      <Step>
        <H>Slope between two points</H>
        <P>
          We already know how to get the slope between two points: rise over run. Pick a point on the curve at <M>x</M> and a
          second one a distance <M>h</M> further along. The line through both is called a <strong>secant</strong>:
        </P>
        <MB>{'\\text{slope} \\approx \\frac{f(x + \\blue{h}) - f(x)}{\\blue{h}}'}</MB>
        <Figure n="4.1" caption="A secant line through two points on a curve. Slide h toward zero and watch the secant settle onto the curve’s true slope at x.">
          <SecantToTangent />
        </Figure>
        <P>
          As <M>h</M> shrinks, the second point slides toward the first, and the secant line turns into the{' '}
          <strong>tangent</strong> — the line that just grazes the curve at that point. Its slope is the derivative.
        </P>
      </Step>

      <Step gate="q-zoom">
        <H>Zoom in until it’s straight</H>
        <P>
          Here’s another way to see it. Smooth curves have a magical property: <strong>zoom in far enough on any point and the
          curve looks like a straight line</strong>. The derivative is simply the slope of that line.
        </P>
        <Figure n="4.2" caption="Zoom into one point of a curve. At 200×, the curve and its tangent line (dashed) are indistinguishable.">
          <LocalLinearity />
        </Figure>
        <Callout kind="plain">
          This is why calculus works for machine learning: no matter how complicated the loss is, if you only take a{' '}
          <em>small</em> step, it behaves like a straight line — and straight lines are easy.
        </Callout>
        <Quiz
          id="q-zoom"
          question="Why does zooming in on a smooth curve help us find its slope?"
          options={[
            { label: 'Up close, the curve is almost a straight line, and straight lines have a single slope.', correct: true },
            { label: 'Zooming makes the numbers more precise.', why: 'It’s not about precision — it’s about the shape becoming straight.' },
            { label: 'Curves are made of tiny straight segments.', why: 'Close — but they’re not literally made of segments; they just look straight when magnified enough.' },
          ]}
        />
      </Step>

      <Step gate="q-notation">
        <H>Notation</H>
        <P>Mathematicians write the result of shrinking h all the way to zero like this:</P>
        <MB>{"f'(x) = \\frac{df}{dx} = \\lim_{h \\to 0} \\frac{f(x+h) - f(x)}{h}"}</MB>
        <Callout kind="notation">
          <M>{"f'(x)"}</M> (“f prime of x”) and <M>{'\\tfrac{df}{dx}'}</M> (“d f by d x”) both mean “the derivative of f”.{' '}
          <M>{'\\lim_{h \\to 0}'}</M> means “what this gets closer and closer to as h shrinks toward 0”. The <em>d</em> stands
          for a tiny change: <M>{'\\tfrac{df}{dx}'}</M> = tiny change in f ÷ tiny change in x.
        </Callout>
        <Exercise id="shrinking-h" title="Watch the slope settle" {...EX.shrinkingH} />
        <Quiz
          id="q-notation"
          question={<>The derivative of a function at some point is <M>{"f'(2) = -3"}</M>. What happens if you increase x a tiny bit from 2?</>}
          options={[
            { label: 'f goes down — about 3 times as much as x went up.', correct: true },
            { label: 'f goes up by 3.', why: 'A negative slope means the function goes downhill as x increases.' },
            { label: 'f becomes −3.', why: 'The derivative is the slope, not the value of f itself.' },
          ]}
          explain="Slope −3: nudge x up by 0.01 and f drops by about 0.03. The sign says which direction is downhill; the size says how steep."
        />
      </Step>

      <Step gate="numerical-derivative">
        <H>A derivative calculator</H>
        <P>
          You can compute a derivative numerically with one line: evaluate the function slightly to the right and slightly to
          the left, and divide the difference by the distance. (Taking a step on <em>both</em> sides is noticeably more accurate
          than one.)
        </P>
        <Exercise
          id="numerical-derivative"
          title="derivative(f, x)"
          {...EX.numericalDerivative}
          hints={[
            <>
              <C>return (f(x + h) - f(x - h)) / (2 * h)</C>
            </>,
          ]}
        />
        <Callout kind="why">
          This numerical trick is used to <em>check</em> machine-learning code (“gradient checking”). But for a model with a
          billion parameters, wiggling each one separately is far too slow. The next two lessons give you shortcuts — and the{' '}
          <Hl c="pink">chain rule</Hl>, which computes all the slopes in one sweep.
        </Callout>
      </Step>
    </Lesson>
  )
}
